import type { ApprovalBlocker } from '@deepguide-ai/types'
import { useAskAi } from '@/components/chat/useAskAi'

/**
 * A gap the approver is stuck on. Covers both shapes the approval
 * readiness check returns: `ApprovalBlocker` (hard commitments, gap
 * findings, contradicted claims) and the unlinked-commitment rows, which
 * carry only a claim sentence.
 */
export type AskVeraPolicyGap = {
  /** Short label for the gap kind, e.g. "Hard commitment". */
  label: string
  /** The gap headline — a blocker title or the commitment sentence. */
  title: string
  /** Optional supporting text (the offending policy sentence, control ref). */
  detail?: string | null
  severity?: ApprovalBlocker['severity']
}

/**
 * Opens Vera in the right-hand panel scoped to a single policy-approval
 * gap, seeded with a "how do I fix this" question.
 *
 * This is the interactive counterpart to `assignPolicyFindingToVera`,
 * which queues background work and requires a persisted RemediationTicket.
 * Hard commitments are computed live by `checkOverPromises` and have no
 * finding behind them (`findingKey` is always null), so they could never
 * use that path — chat is what makes them actionable without minting
 * durable records for a transient computation.
 *
 * Mirrors `useAskVeraAboutTest` in the auditor center.
 */
export function useAskVeraAboutPolicyGap(
  workspaceId: string | undefined,
  policySlug: string,
  policyTitle: string
) {
  const askAi = useAskAi()
  return (gap: AskVeraPolicyGap) => {
    const detail = gap.detail?.trim()
    askAi({
      text: [
        `Approval of "${policyTitle}" is blocked by this gap:`,
        '',
        `${gap.label}${gap.severity ? ` (${gap.severity})` : ''}: ${gap.title}`,
        detail ? `\nThe passage in question:\n"${detail}"` : '',
        '',
        'What do I need to change in the policy, or what evidence do I need, to resolve it?',
      ]
        .filter(Boolean)
        .join('\n'),
      pagePath: `/workspaces/${workspaceId}/policies_/${policySlug}`,
      page: 'policy-detail',
      pageData: {
        policySlug,
        policyTitle,
        gapLabel: gap.label,
        gapTitle: gap.title,
        gapDetail: detail || undefined,
      },
      metadata: {
        source: 'ask-vera',
        entityType: 'policy-approval-gap',
        policySlug,
        gapTitle: gap.title,
      },
    })
  }
}
